import React, { useEffect, useState } from "react";
import { Sparkles, Copy, Wand2, FileText, Search, Loader2, Download, RefreshCw, Check, Code2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import Layout, { PageHeader } from "@/components/Layout";
import { getNav } from "@/pages/navConfigs";

export default function ContentCreatorPage() {
  const { user, customRole } = useAuth();
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState({ is_seo_role: false, doc_templates: {}, models: [] });
  const [mode, setMode] = useState("");
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [feedStatus, setFeedStatus] = useState("");
  const [wordLimit, setWordLimit] = useState(400);
  const [docType, setDocType] = useState("proposal");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, t] = await Promise.all([api.get("/ai/config"), api.get("/ai/templates")]);
      setConfig(c.data);
      setTemplates(t.data);
      setMode(t.data.is_seo_role ? "seo" : "document");
      setModel(c.data.model || t.data.models?.[0]?.id || "");
      if (t.data.is_seo_role) loadKeywords();
    })();
    // eslint-disable-next-line
  }, []);

  const loadKeywords = async () => {
    try {
      const r = await api.get("/ai/keywords");
      if (r.data.keywords?.length) {
        setKeywords(r.data.keywords.join(", "));
        setFeedStatus(r.data.message);
      } else {
        setFeedStatus(r.data.message || "No keywords loaded");
      }
    } catch { setFeedStatus("Failed to load feed"); }
  };

  const generate = async () => {
    if (!topic.trim()) return toast.error("Add a topic or instructions");
    setBusy(true);
    setOut(null);
    try {
      const r = await api.post("/ai/generate", { mode, topic, keywords, word_limit: wordLimit, doc_type: docType, model });
      setOut(r.data);
      toast.success("Generated!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!out?.content) return;
    await navigator.clipboard.writeText(out.content);
    toast.success("Markdown copied");
  };

  const copyAsHtml = async () => {
    if (!out?.content) return;
    // Use rendered DOM if available, else send markdown
    const node = document.getElementById("ai-output-rendered");
    const html = node ? node.innerHTML : out.content;
    try {
      const blob = new Blob([html], { type: "text/html" });
      await navigator.clipboard.write([new window.ClipboardItem({ "text/html": blob, "text/plain": new Blob([node?.innerText || out.content], { type: "text/plain" }) })]);
      toast.success("Formatted content copied — paste into Docs, Word, email…");
    } catch {
      await navigator.clipboard.writeText(html);
      toast.success("HTML copied");
    }
  };

  const downloadMd = () => {
    if (!out?.content) return;
    const blob = new Blob([out.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(topic || "content").slice(0, 40).replace(/\s+/g, "_")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveAsBlog = async () => {
    if (!out?.content) return;
    await api.post("/blogs", { title: topic.slice(0, 80), content_html: out.content.replace(/\n/g, "<br>") });
    toast.success("Saved as blog");
  };

  if (!config) return <Layout navItems={getNav(user.role, customRole)}><div className="p-12 text-slate-400">Loading…</div></Layout>;

  const showSeoControls = templates.is_seo_role && mode === "seo";
  const showDocControls = mode === "document";

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto">
        <PageHeader title={mode === "document" ? "AI Document Generator" : "AI Content Creator"}
          subtitle={mode === "document" ? "Polished proposals, letters & event documents" : "SEO-optimised copy via free OpenRouter models"}
          icon={Wand2} accent="violet" />

        {!config.configured && (
          <div className="dashboard-card p-5 mb-5 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold">
              <Sparkles className="w-4 h-4" /> OpenRouter is not configured yet.
              {user.role === "admin" ? <span> Go to <b>API & Integrations → Configure OpenRouter</b>.</span> : <span> Ask your admin to add an OpenRouter API key.</span>}
            </div>
          </div>
        )}

        <div className="dashboard-card p-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {templates.is_seo_role && (
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Strategy mode</label>
                <select className="input-pill mt-1" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="ai-mode">
                  <option value="seo">SEO Optimization (with keywords)</option>
                  <option value="document">Pure Editorial / Document</option>
                </select>
              </div>
            )}
            {showDocControls && (
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Document type</label>
                <select className="input-pill mt-1" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="ai-doc-type">
                  {Object.entries(templates.doc_templates).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Target length (words)</label>
              <input type="number" min={50} max={3000} step={50} className="input-pill mt-1" value={wordLimit} onChange={(e) => setWordLimit(parseInt(e.target.value) || 400)} data-testid="ai-words" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Model (free)</label>
              <select className="input-pill mt-1" value={model} onChange={(e) => setModel(e.target.value)} data-testid="ai-model">
                {templates.models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          </div>

          {showSeoControls && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">SEO keywords (auto-fetched from admin feed)</label>
                <button type="button" onClick={loadKeywords} className="text-xs text-orange-600 inline-flex items-center gap-1" data-testid="ai-refresh-keywords"><RefreshCw className="w-3 h-3" /> Refresh feed</button>
              </div>
              <input className="input-pill" placeholder="Comma-separated keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="ai-keywords" />
              {feedStatus && <div className="text-xs text-slate-500 mt-1">{feedStatus}</div>}
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase text-slate-500 tracking-wider">Topic / instructions / bullets</label>
            <textarea rows={6} className="input-pill mt-1 font-mono text-sm" placeholder={templates.is_seo_role ? "Topic, audience, angles, anything you'd brief a copywriter…" : "Event name, dates, key points, target audience for this document…"} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="ai-topic" />
          </div>

          <button onClick={generate} disabled={busy || !config.configured} className="btn-primary w-full py-4 text-base" data-testid="ai-generate">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate {templates.is_seo_role ? "copy" : "document"}</>}
          </button>
        </div>

        {out && (
          <div className="dashboard-card p-0 mt-5 overflow-hidden">
            {/* Top bar with metrics + copy actions */}
            <div className="px-6 py-4 bg-gradient-to-r from-orange-50 via-amber-50 to-pink-50 border-b border-slate-100">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-xs">
                  <span className="role-pill bg-white text-slate-700 border border-slate-200">{out.word_count} words</span>
                  {out.keyword_integrations !== null && out.keyword_integrations !== undefined && (
                    <>
                      <span className="role-pill bg-indigo-100 text-indigo-700">{out.keyword_integrations} keywords integrated</span>
                      <span className="role-pill bg-emerald-100 text-emerald-700">{out.seo_density}% SEO density</span>
                    </>
                  )}
                  <span className="text-slate-500 hidden md:inline">· {out.model}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={copy} className="btn-ghost text-xs py-2" data-testid="ai-copy-md"><Copy className="w-3 h-3" /> Copy Markdown</button>
                  <button onClick={copyAsHtml} className="btn-primary text-xs py-2" data-testid="ai-copy-rich"><Check className="w-3 h-3" /> Copy formatted</button>
                  <button onClick={downloadMd} className="btn-ghost text-xs py-2" data-testid="ai-download-md"><Download className="w-3 h-3" /> .md</button>
                  {user.role === "content_writer" && <button onClick={saveAsBlog} className="btn-ghost text-xs py-2"><FileText className="w-3 h-3" /> Save as blog</button>}
                </div>
              </div>
            </div>

            {/* Rendered content — beautiful typography */}
            <article id="ai-output-rendered" className="ai-output px-8 py-8" data-testid="ai-output">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{out.content}</ReactMarkdown>
            </article>
          </div>
        )}
      </div>
    </Layout>
  );
}
